/**
 * FullscreenView: modal hiển thị 1 employee screen FULL SIZE.
 *
 * Minimal layout: ảnh full screen + nút X góc phải-trên.
 * Phím `H` toggle info overlay (App/Window/URL/Last update) góc dưới.
 *
 * Bypass <Dialog>: dùng <dialog> native để fullscreen toàn viewport,
 * không bị giới hạn bởi Dialog component (max-w-[1400px], mt-[5vh]).
 *
 * Same double-buffer + crossfade pattern as ScreenTile — modal swaps
 * images without flashing.
 */
import { useEffect, useRef, useState } from 'react'
import { formatDateTime } from '@/lib/format'
import { resolveScreenSrc } from '@/lib/axios'
import { cn } from '@/lib/cn'
import type { Employee } from '@monitor/shared'
import { useScreen } from '@/hooks/use-screen-store'

const FADE_MS = 220

interface FullscreenViewProps {
  employee: Employee | null
  open: boolean
  onClose: () => void
}

export function FullscreenView({ employee, open, onClose }: FullscreenViewProps) {
  const live = useScreen(employee?.id ?? -1)

  // Same zero-cost realtime as ScreenTile: dataBase64 first, file URL
  // with correct cache-bust otherwise.
  const newestUrl = live.latestScreen ? resolveScreenSrc(live.latestScreen) : null

  const [bottomUrl, setBottomUrl] = useState<string | null>(null)
  const [topUrl, setTopUrl] = useState<string | null>(null)
  const [topLoaded, setTopLoaded] = useState(false)
  const [infoVisible, setInfoVisible] = useState(false)
  // True while we are waiting for the first screen event of the
  // currently-selected employee. While true we keep showing the previous
  // frame (stale fallback) so the modal doesn't flash "Chua nhan duoc anh"
  // every time the user clicks a different tile.
  const [waiting, setWaiting] = useState(false)

  const dlgRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    if (!newestUrl || newestUrl === topUrl) return
    setTopUrl(newestUrl)
    setTopLoaded(false)
    setWaiting(false)
  }, [newestUrl, topUrl])

  useEffect(() => {
    if (!topUrl || !topLoaded) return
    const t = setTimeout(() => {
      setBottomUrl(topUrl)
      setTopUrl(null)
      setTopLoaded(false)
    }, FADE_MS)
    return () => clearTimeout(t)
  }, [topUrl, topLoaded])

  // open/close native dialog
  useEffect(() => {
    const dlg = dlgRef.current
    if (!dlg) return
    if (open && !dlg.open) dlg.showModal()
    if (!open && dlg.open) dlg.close()
  }, [open])

  // Reset info overlay each time modal closes so next open is clean
  useEffect(() => {
    if (!open) setInfoVisible(false)
  }, [open])

  // When the selected employee changes, decide whether to keep showing
  // the previous frame (stale fallback) or clear the buffers.
  //
  // - If the new employee ALREADY has a frame in the store, set it as
  //   the visible image so there's no flash. The next screen event
  //   (cadence 2s) will crossfade in to update it.
  // - If the new employee has NEVER streamed, clear the buffers so the
  //   user sees the "Chua nhan duoc anh" message instead of the previous
  //   employee's desktop.
  useEffect(() => {
    if (live.latestScreen) {
      const url = resolveScreenSrc(live.latestScreen)
      // Only re-init the buffer if the URL actually differs from the
      // current one. Otherwise every 2s screen event would reset
      // `bottomUrl` and force the <img> to reload, causing a flash.
      setBottomUrl((prev) => (prev === url ? prev : url))
      setTopUrl(null)
      setTopLoaded(false)
    } else {
      setBottomUrl(null)
      setTopUrl(null)
      setTopLoaded(false)
    }
    setWaiting(true)
  }, [employee?.id, live.latestScreen])

  // keyboard: Escape closes, H toggles info overlay
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'h' || e.key === 'H') {
        setInfoVisible((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const activity = live.latestScreen?.activity
  const isOnline = live.isOnline

  return (
    <dialog
      ref={dlgRef}
      className="h-screen w-screen max-h-none max-w-none bg-transparent p-0 m-0 backdrop:bg-black/90"
      onCancel={() => onClose()}
      onClose={() => onClose()}
      onClick={(e) => {
        // click on the dialog backdrop (outside the inner box) closes it
        if (e.target === dlgRef.current) onClose()
      }}
    >
      <div className="relative flex h-screen w-screen items-center justify-center overflow-hidden bg-black">
        {/* Empty state — only when there's no fallback frame AND we're
            waiting for the first screen event. (If we have a stale frame
            from the previous employee, keep showing it instead.) */}
        {!bottomUrl && !topUrl && waiting && (
          <div className="flex h-[60vh] items-center justify-center text-slate-500">
            Chưa nhận được ảnh từ agent.
          </div>
        )}

        {bottomUrl && (
          <img
            src={bottomUrl}
            alt={`Screen of ${employee?.fullName ?? ''}`}
            className="absolute inset-0 h-full w-full object-contain"
            draggable={false}
          />
        )}
        {topUrl && (
          <img
            src={topUrl}
            alt=""
            onLoad={() => setTopLoaded(true)}
            className={cn(
              'absolute inset-0 h-full w-full object-contain transition-opacity',
              topLoaded ? 'opacity-100' : 'opacity-0',
            )}
            style={{ transitionDuration: `${FADE_MS}ms` }}
            draggable={false}
          />
        )}

        {/* Close button — top-right, always visible */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Đóng"
          className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-xl text-white shadow-lg backdrop-blur transition-colors hover:bg-black/80"
        >
          ✕
        </button>

        {/* Optional info overlay (toggled with H) */}
        {infoVisible && (
          <div className="pointer-events-none absolute left-1/2 bottom-6 z-10 -translate-x-1/2 rounded-lg border border-white/10 bg-black/70 px-4 py-3 text-xs text-white shadow-lg backdrop-blur">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
              {employee && (
                <>
                  <div className="font-semibold text-white">
                    {employee.fullName}
                    <span className="ml-2 text-white/60">{employee.employeeCode}</span>
                  </div>
                  <div
                    className={cn(
                      'rounded px-1.5 py-0.5 text-[10px] font-medium',
                      isOnline ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-300',
                    )}
                  >
                    {isOnline ? 'Online' : 'Offline'}
                  </div>
                </>
              )}
              <div>
                <span className="text-white/50">App: </span>
                <span className="font-medium">{activity?.appName ?? '—'}</span>
              </div>
              <div className="max-w-[28rem] truncate">
                <span className="text-white/50">Window: </span>
                <span className="font-medium" title={activity?.windowTitle ?? ''}>
                  {activity?.windowTitle ?? '—'}
                </span>
              </div>
              <div className="max-w-[28rem] truncate">
                <span className="text-white/50">URL: </span>
                <span className="font-medium" title={activity?.url ?? ''}>
                  {activity?.url ?? '—'}
                </span>
              </div>
              <div>
                <span className="text-white/50">Last: </span>
                <span className="font-medium">{formatDateTime(live.lastHeartbeat)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </dialog>
  )
}
